import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthProvider";

type Item = {
  id: string;
  code: string;
  value: string;
  short_name?: string | null;
  sort_order: number;
  is_active: boolean;
};

type Column = "code" | "value" | "short_name" | "sort_order" | "is_active";

interface Props {
  title: string;
  listUrl: string;         // GET (returnează toate, active + inactive)
  createUrl: string;       // POST
  updateUrl: (code: string) => string; // PATCH
  // deleteUrl rămâne dar nu-l mai folosim; toggle-ul face PATCH la is_active
  deleteUrl?: (code: string) => string;
  columns?: Column[];
}

const GenericTaxonomyManager: React.FC<Props> = ({
  title,
  listUrl,
  createUrl,
  updateUrl,
  deleteUrl,
  columns = ["code", "value", "short_name", "sort_order", "is_active"],
}) => {
  const { token } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem(`showInactive_${title}`) === "true";
  });
  const [form, setForm] = useState<{ code: string; value: string; short_name: string; sort_order: number; is_active: boolean }>({
    code: "",
    value: "",
    short_name: "",
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    localStorage.setItem(`showInactive_${title}`, String(showInactive));
  }, [showInactive, title]);

  const Toast = Swal.mixin({
    toast: true, position: "top-end", timer: 1800, showConfirmButton: false,
  });

  const normalizeItem = (raw: any, idx: number): Item => {
    const code = (raw?.code ?? raw?.key ?? "").toString();
    const value = (raw?.value ?? raw?.name ?? "").toString();
    const short_name = raw?.short_name ?? raw?.shortName ?? null;
    const sort_order = Number(raw?.sort_order ?? raw?.priority ?? 0);
    const is_active = raw?.is_active ?? raw?.active ?? true;
    const id = (raw?.id ?? code ?? raw?.key ?? String(idx)).toString();
    return { id, code, value, short_name, sort_order, is_active };
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(listUrl, { headers: { Authorization: `Bearer ${token}` }});
      const data = await res.json();
      const arr = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.items) ? data.items
        : null;

      if (!arr) throw new Error("Răspuns invalid (nu e listă).");
      const normalized = arr.map((row: any, i: number) => normalizeItem(row, i));
      // sortare default: sort_order, apoi value
      normalized.sort((a: Item, b: Item) => (a.sort_order - b.sort_order) || a.value.localeCompare(b.value));
      setItems(normalized);
    } catch (e: any) {
      console.error(e);
      Swal.fire("Eroare", e.message || "Nu s-a putut încărca lista.", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchItems(); }, [token]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.value) {
      Toast.fire({icon:"error", title:"Completează key și value"});
      return;
    }
    try {
      const payload = {
        code: form.code.trim(),
        value: form.value.trim(),
        short_name: form.short_name?.trim() || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: !!form.is_active,
      };
      const r = await fetch(createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const { detail } = await r.json().catch(() => ({}));
        throw new Error(detail || "Eroare la creare.");
      }
      setShowAdd(false);
      setForm({ code: "", value: "", short_name: "", sort_order: 0, is_active: true });
      Toast.fire({ icon: "success", title: "Creat!" });
      fetchItems();
    } catch (err: any) {
      Swal.fire("Eroare", err.message || "Eroare la creare.", "error");
    }
  };

  const onUpdateField = async (code: string, field: keyof Item, value: any) => {
    try {
      if (field === "code") return;
      const payload: any = { [field]: field === "sort_order" ? Number(value) : value };

      // optimist update
      setItems(prev => prev.map(i => i.code === code ? { ...i, ...payload } : i));

      const r = await fetch(updateUrl(code), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const { detail } = await r.json().catch(() => ({}));
        throw new Error(detail || "Eroare la actualizare.");
      }
      Toast.fire({ icon: "success", title: "Salvat!" });
    } catch (err: any) {
      Swal.fire("Eroare", err.message || "Eroare la actualizare.", "error");
      // refetch ca să revenim dacă a eșuat
      fetchItems();
    }
  };

  // listă vizibilă în funcție de filtrul "showInactive"
  const visibleItems = useMemo(
    () => (showInactive ? items : items.filter(i => i.is_active)),
    [items, showInactive]
  );

  return (
    <div className="box-card">
      <div className="box-head">
        <h2 className="page-subtitle">{title}</h2>
        <div className="box-actions">
            <div className="toggle-switch">
            
                <input
                id="toggleSwitch"
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                />
            <label htmlFor="toggleSwitch">
                <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">{showInactive ? "Arată inactivi" : "Arată activi"}</span>
            </div>
          <button className="primary-btn" onClick={() => setShowAdd(true)}>Adaugă</button>
        </div>
      </div>

      {showAdd && (
        <form className="form-row grid-2 add-setting-form" onSubmit={onCreate}>
          {columns.includes("code") && (
            <div>
              <label>Key</label>
              <input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="ex: evaluare"
                required
              />
            </div>
          )}
          {columns.includes("value") && (
            <div>
              <label>Denumire</label>
              <input
                value={form.value}
                onChange={e => setForm({ ...form, value: e.target.value })}
                placeholder="ex: Evaluare"
                required
              />
            </div>
          )}
          {columns.includes("short_name") && (
            <div>
              <label>Prescurtare</label>
              <input
                value={form.short_name}
                onChange={e => setForm({ ...form, short_name: e.target.value })}
                placeholder="ex: EV"
              />
            </div>
          )}
          {columns.includes("sort_order") && (
            <div>
              <label>Nr. ordine</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={e => setForm({ ...form, sort_order: Number(e.target.value) })}
              />
            </div>
          )}
          {columns.includes("is_active") && (
            <div className="checkbox-row">
              <label>Activ</label>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
            </div>
          )}
          <div className="form-actions">
            <button className="cancel-btn" onClick={() => setShowAdd(false)} type="button">Anulează</button>
            <button className="primary-btn" type="submit">Salvează</button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Se încarcă…</p>
      ) : visibleItems.length === 0 ? (
        <p>Nu există elemente.</p>
      ) : (
        <div className="table-rounded-container">
          <table className="table-rounded settings-table">
            <thead>
              <tr>
                {columns.includes("code") && <th>Key</th>}
                {columns.includes("value") && <th>Denumire</th>}
                {columns.includes("short_name") && <th>Prescurtare</th>}
                {columns.includes("sort_order") && <th>Prioritate</th>}
                {columns.includes("is_active") && <th>Activ</th>}
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((it) => (
                <tr key={it.id} className={!it.is_active ? "row-inactive" : undefined}>
                  {columns.includes("code") && <td><code>{it.code}</code></td>}
                  {columns.includes("value") && (
                    <td>
                      <input
                        value={it.value}
                        onChange={(e) => setItems(prev => prev.map(p => p.id === it.id ? { ...p, value: e.target.value } : p))}
                        onBlur={(e) => onUpdateField(it.code, "value", e.target.value)}
                      />
                    </td>
                  )}
                  {columns.includes("short_name") && (
                    <td>
                      <input
                        value={it.short_name ?? ""}
                        onChange={(e) => setItems(prev => prev.map(p => p.id === it.id ? { ...p, short_name: e.target.value } : p))}
                        onBlur={(e) => onUpdateField(it.code, "short_name", e.target.value || null)}
                      />
                    </td>
                  )}
                  {columns.includes("sort_order") && (
                    <td>
                      <input
                        type="number"
                        value={it.sort_order}
                        onChange={(e) => setItems(prev => prev.map(p => p.id === it.id ? { ...p, sort_order: Number(e.target.value) } : p))}
                        onBlur={(e) => onUpdateField(it.code, "sort_order", Number(e.target.value))}
                        style={{ width: 80 }}
                      />
                    </td>
                  )}
                  {columns.includes("is_active") && (
                    <td>
                      <button
                        className={it.is_active ? "active" : "inactive"}
                        onClick={() => onUpdateField(it.code, "is_active", !it.is_active)}
                        title="Activează/Dezactivează"
                      >
                        {it.is_active ? "Activ" : "Inactiv"} <span className="button-dot">•</span>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* stil mic pt rând inactiv (poți muta în CSS global) */}
      <style>{`
        .row-inactive { opacity: .6; }
        .box-head .box-actions { display: flex; gap: 12px; align-items: center; }
        .toggle-inactive input { vertical-align: middle; }
        .button-dot { margin-left: 6px; }
      `}</style>
    </div>
  );
};

export default GenericTaxonomyManager;